import { Trans } from '@lingui/react/macro';
import { SwapCallsOutlined } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { useMemo } from 'react';
import { Button } from 'ui-kit';
import { useBridgeAlertState } from '../../features/terminal/hooks/useBridgeAlertState';
import { type BridgeCheckoutCallbacks, useBridgeCheckoutStore } from './bridge_checkout_store';
import { ASSET_TICKER, AUSD_ADDRESS } from './constants';

export { AUSD_ADDRESS, ASSET_TICKER };

const BRIDGE_ALERT_SX = {
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
    animation: 'sheen 4s ease-in-out infinite',
  },
};

interface FunCheckoutButtonProps extends BridgeCheckoutCallbacks {
  sx?: any;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  children?: React.ReactNode;
  onBeforeOpen?: () => void;
}

export function FunCheckoutButton({
  sx,
  variant = 'contained',
  size,
  fullWidth = false,
  children,
  onSuccess,
  onClose,
  source,
  onBeforeOpen,
}: FunCheckoutButtonProps) {
  const requestCheckout = useBridgeCheckoutStore((state) => state.requestCheckout);
  const shouldShowBridgeAlert = useBridgeAlertState();

  const finalSx = useMemo(() => {
    if (shouldShowBridgeAlert) {
      return [sx, BRIDGE_ALERT_SX];
    }
    return sx;
  }, [sx, shouldShowBridgeAlert]);

  const handleClick = () => {
    onBeforeOpen?.();
    requestCheckout({ onSuccess, onClose, source });
  };

  return (
    <Button onClick={handleClick} variant={variant} size={size} sx={finalSx} fullWidth={fullWidth}>
      {children ?? <Trans comment="Button opening the bridge checkout">Bridge</Trans>}
    </Button>
  );
}

interface FunBridgeMenuItemProps extends BridgeCheckoutCallbacks {
  disabled?: boolean;
  onBeforeOpen?: () => void;
}

export function FunBridgeMenuItem({ disabled = false, onBeforeOpen, onSuccess, onClose, source }: FunBridgeMenuItemProps) {
  const requestCheckout = useBridgeCheckoutStore((state) => state.requestCheckout);

  const handleClick = () => {
    onBeforeOpen?.();
    requestCheckout({ onSuccess, onClose, source });
  };

  return (
    <MenuItem onClick={handleClick} disabled={disabled}>
      <ListItemIcon>
        <SwapCallsOutlined fontSize="large" />
      </ListItemIcon>
      <ListItemText>
        <Trans comment="Button opening the bridge checkout">Bridge</Trans>
      </ListItemText>
    </MenuItem>
  );
}
